import { Dialog } from "@radix-ui/react-dialog"
import {
  Button,
  DialogBody,
  DialogContent,
  IconButton,
  Typography,
} from "@suraasa/placebo-ui"
import AsyncBuilder from "components/AsyncBuilder"
import { Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import { pluralize } from "utils/helpers"

import SingleAiSparkle from "../assets/SingleAiSparkle"
import { useSuraasaPlusContext } from "../context"
import { getSuraasaPlusLandingPageUrl } from "../helper"
import { getTimeUntilMidnightUTC } from "./helper"

type Props = {
  open: boolean
  handleClose: () => void
}

const GenerationLimitReachedDialog = ({ open, handleClose }: Props) => {
  const {
    isFreeTrialAvailable,
    aiToolGenerationsAllowed,
    initiateSuraasaPlusPurchaseFlow,
  } = useSuraasaPlusContext()
  const [timeLeft, setTimeLeft] = useState(getTimeUntilMidnightUTC())

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeUntilMidnightUTC())
    }, 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  if (aiToolGenerationsAllowed !== undefined)
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        {/* eslint-disable-next-line @cspell/spellchecker */}
        <DialogContent className="!w-[370px] rounded-lg bg-[radial-gradient(135.13%_99.81%_at_79.29%_99.91%,#DDEEFD_0%,#FFFCE8_24.57%,#FFFFFF_47.74%)]">
          <DialogBody className="p-2.5">
            <div className="mb-1.25 flex flex-row items-center justify-between">
              <div className="rounded-[9px] bg-[#DAE2EB] p-[7px]">
                <SingleAiSparkle height={15} width={15} fill="#FFFFFF" />
              </div>
              <IconButton
                onClick={() => handleClose()}
                className="text-onSurface-600"
              >
                <Xmark />
              </IconButton>
            </div>
            <Typography variant="title4" className="text-onSurface-900">
              Daily Limit Reached
            </Typography>
            <div className="my-2.5">
              <Typography className="text-onSurface-700" variant="body">
                You&apos;ve used your free daily{" "}
                {pluralize("generation", aiToolGenerationsAllowed, {
                  skipCount: true,
                })}
                . Upgrade to <span className="text-strong">Suraasa Plus </span>
                for unlimited access.
              </Typography>
              <Typography className="mt-2 text-onSurface-700" variant="body">
                Limit resets in <span className="text-strong">{timeLeft}</span>
              </Typography>
            </div>
            <div className="flex flex-row items-center gap-1.5">
              <Button className="shrink-0 rounded-xl bg-[#94A3B81A]/10 text-onSurface-700">
                <a
                  href={getSuraasaPlusLandingPageUrl()}
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn More
                </a>
              </Button>
              <AsyncBuilder
                handler={async () => {
                  handleClose()
                  await initiateSuraasaPlusPurchaseFlow()
                }}
                render={props => (
                  <Button
                    loading={props.loading}
                    onClick={props.onClick}
                    color="black"
                    className="w-full rounded-xl"
                  >
                    {isFreeTrialAvailable
                      ? "Try Suraasa Plus"
                      : "Get Suraasa Plus"}
                  </Button>
                )}
              />
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    )
}

export default GenerationLimitReachedDialog
